import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, apiJson } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';

interface RedeemSuccess {
  enrollment: { id: string; courseId: string };
  redeemedInviteId?: string;
  reactivated?: boolean;
}

interface PendingRedeemSuccess {
  ok: boolean;
  courseId: string;
}

export function CallbackRoute() {
  const { t } = useTranslation();
  const { user, loading, refresh } = useSession();
  const navigate = useNavigate();

  const [params] = useState(() => {
    if (typeof window === 'undefined') return { inviteToken: null, pendingInviteId: null };
    const sp = new URLSearchParams(window.location.search);
    return {
      inviteToken: sp.get('inviteToken'),
      pendingInviteId: sp.get('pendingInviteId'),
    };
  });
  const [error, setError] = useState<string | null>(null);
  const redeemStartedRef = useRef(false);

  useEffect(() => { void refresh(); }, [refresh]);

  useEffect(() => {
    if (loading) return;

    // No invite/pending token: just redirect to dashboard
    if (!params.inviteToken && !params.pendingInviteId) {
      if (user) {
        void navigate({ to: user.role === 'creator' ? '/creator' : '/student' });
      } else {
        void navigate({ to: '/' });
      }
      return;
    }

    if (redeemStartedRef.current) return;
    redeemStartedRef.current = true;

    const run = async () => {
      try {
        if (params.inviteToken) {
          // Legacy invite token flow
          const res = await apiJson<RedeemSuccess>('/api/invites/redeem', {
            method: 'POST',
            body: JSON.stringify({ token: params.inviteToken }),
          });
          void navigate({
            to: '/student/courses/$courseId',
            params: { courseId: res.enrollment.courseId },
          });
        } else if (params.pendingInviteId) {
          // Public landing magic-link enroll flow
          const res = await apiJson<PendingRedeemSuccess>('/api/public/auth/redeem-pending-invite', {
            method: 'POST',
            body: JSON.stringify({ pendingInviteId: params.pendingInviteId }),
          });
          void navigate({
            to: '/student/courses/$courseId',
            params: { courseId: res.courseId },
          });
        }
      } catch (err) {
        if (err instanceof ApiError) {
          const body = err.body as { error?: string; message?: string } | undefined;
          setError(body?.message ?? body?.error ?? `redeem failed (${err.status})`);
          return;
        }
        setError(err instanceof Error ? err.message : 'failed to redeem invite');
      }
    };
    void run();
  }, [user, loading, navigate, params]);

  if (error) {
    return (
      <main className="max-w-md mx-auto px-6 py-16">
        <div className="rounded-lg border border-error bg-error-soft px-4 py-3 text-[14px] text-error" data-testid="invite-error">
          {error}
        </div>
      </main>
    );
  }

  return (
    <main className="max-w-md mx-auto px-6 py-16">
      <p className="text-fg-3 text-[14px]" data-testid="callback-pending">
        {params.inviteToken || params.pendingInviteId ? t('callback.activatingInvite') : t('callback.signingIn')}
      </p>
    </main>
  );
}
